import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section, CanvasElement, ElementType } from './templateSerializer';
import ResizableCanvasItem from './ResizableCanvasItem';
import './templateBuilder.css';

interface CanvasPanelProps {
  sections: Section[];
  onUpdateSections: (sections: Section[]) => void;
  onEditElement: (element: CanvasElement, sectionId: string, maxCols: number) => void;
  onLoadSample: () => void;
}

const CanvasPanel: React.FC<CanvasPanelProps> = ({
  sections,
  onUpdateSections,
  onEditElement,
  onLoadSample,
}) => {
  const [dragOverSectionId, setDragOverSectionId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [collapsedSecIds, setCollapsedSecIds] = useState<Record<string, boolean>>({});

  const showNotification = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const toggleCollapseSection = (secId: string) => {
    setCollapsedSecIds((prev) => {
      const isCurrentlyCollapsed = !!prev[secId];
      const nextState = { ...prev, [secId]: !isCurrentlyCollapsed };
      showNotification(
        !isCurrentlyCollapsed ? '📁 Section minimized' : '📂 Section expanded'
      );
      return nextState;
    });
  };

  // Section Handlers
  const handleAddSection = () => {
    const newSec: Section = {
      id: `sec-${Date.now()}`,
      title: `SECTION ${sections.length + 1}`,
      cols: 6,
      gap: 'gap-6',
      marginBottom: 'mb-4',
      showDivider: sections.length > 0,
      items: [],
    };
    onUpdateSections([...sections, newSec]);
    showNotification(`➕ Added new Section ${sections.length + 1}`);
  };

  const handleRemoveSection = (secId: string) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      onUpdateSections(sections.filter((s) => s.id !== secId));
      showNotification(`🗑️ Deleted Section`);
    }
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= sections.length) return;
    const updated = [...sections];
    const temp = updated[index];
    updated[index] = updated[targetIdx];
    updated[targetIdx] = temp;
    onUpdateSections(updated);
    showNotification(`↔️ Moved Section ${direction === 'up' ? 'Up' : 'Down'}`);
  };

  const handleSectionTitleChange = (secId: string, title: string) => {
    onUpdateSections(
      sections.map((sec) => (sec.id === secId ? { ...sec, title } : sec))
    );
  };

  // Item Handlers
  const handleRemoveItem = (secId: string, itemId: string) => {
    onUpdateSections(
      sections.map((sec) => {
        if (sec.id === secId) {
          return { ...sec, items: sec.items.filter((item) => item.id !== itemId) };
        }
        return sec;
      })
    );
    showNotification(`🗑️ Removed item from canvas`);
  };

  const handleDuplicateItem = (secId: string, item: CanvasElement) => {
    const newItem: CanvasElement = {
      ...item,
      id: `elem-${Date.now()}`,
    };
    onUpdateSections(
      sections.map((sec) => {
        if (sec.id === secId) {
          const itemIdx = sec.items.findIndex((i) => i.id === item.id);
          const newItems = [...sec.items];
          newItems.splice(itemIdx + 1, 0, newItem);
          return { ...sec, items: newItems };
        }
        return sec;
      })
    );
    showNotification(`📋 Duplicated element`);
  };

  const handleUpdateSpan = (secId: string, itemId: string, newSpan: number) => {
    onUpdateSections(
      sections.map((sec) => {
        if (sec.id === secId) {
          return {
            ...sec,
            items: sec.items.map((item) => {
              if (item.id === itemId) {
                return { ...item, colSpan: Math.max(1, Math.min(6, newSpan)) };
              }
              return item;
            }),
          };
        }
        return sec;
      })
    );
    showNotification(`📐 Resized column width to ${Math.round((newSpan / 6) * 100)}%`);
  };

  const handleUpdateContent = (secId: string, itemId: string, content: string) => {
    onUpdateSections(
      sections.map((sec) => {
        if (sec.id === secId) {
          return {
            ...sec,
            items: sec.items.map((item) => {
              if (item.id === itemId) {
                return { ...item, content };
              }
              return item;
            }),
          };
        }
        return sec;
      })
    );
  };

  const handleToggleIf = (secId: string, itemId: string) => {
    onUpdateSections(
      sections.map((sec) => {
        if (sec.id === secId) {
          return {
            ...sec,
            items: sec.items.map((item) => {
              if (item.id === itemId) {
                const currentVal = item.enableIf !== false;
                return { ...item, enableIf: !currentVal };
              }
              return item;
            }),
          };
        }
        return sec;
      })
    );
  };


  // REORDERING CANVAS ITEMS WITHIN OR ACROSS SECTIONS WITH NEW ROW SUPPORT & TOAST NOTIFICATION
  const handleReorderItems = (
    sourceSecId: string,
    targetSecId: string,
    sourceIndex: number,
    targetIndex: number,
    isBeside: boolean = false
  ) => {
    let updatedSections = sections.map((sec) => ({
      ...sec,
      items: sec.items.map((item) => ({ ...item })),
    }));

    const sourceSec = updatedSections.find((s) => s.id === sourceSecId);
    const targetSec = updatedSections.find((s) => s.id === targetSecId);

    if (!sourceSec || !targetSec) return;

    // Remove source item
    const [movedItem] = sourceSec.items.splice(sourceIndex, 1);

    // Adjust target index when reordering within the same section & source was before target
    let adjustedTargetIndex = targetIndex;
    if (sourceSecId === targetSecId && sourceIndex < targetIndex) {
      adjustedTargetIndex = targetIndex - 1;
    }
    adjustedTargetIndex = Math.max(0, Math.min(targetSec.items.length, adjustedTargetIndex));

    const itemName = movedItem.keyName || movedItem.content || movedItem.keyCode || 'Element';

    if (isBeside) {
      // Placing BESIDE -> remove forceNewRow flag
      movedItem.forceNewRow = false;

      if (targetSec.items.length > 0) {
        const neighborIndex = Math.max(
          0,
          Math.min(
            targetSec.items.length - 1,
            adjustedTargetIndex === 0 ? 0 : adjustedTargetIndex - 1
          )
        );
        const neighborItem = targetSec.items[neighborIndex];
        if (neighborItem) {
          const movedSpan = movedItem.colSpan || 3;
          const neighborSpan = neighborItem.colSpan || 3;

          if (movedSpan + neighborSpan > 6) {
            movedItem.colSpan = 3;
            neighborItem.colSpan = 3;
          }
        }
      }
      showNotification(`✨ Placed "${itemName}" BESIDE on the same row!`);
    } else {
      // Dropping BELOW -> set forceNewRow flag so it breaks onto a new row below
      movedItem.forceNewRow = true;
      showNotification(`🚀 Placed "${itemName}" on a NEW ROW BELOW!`);
    }

    // Insert moved item at exact adjusted target position
    targetSec.items.splice(adjustedTargetIndex, 0, movedItem);

    onUpdateSections(updatedSections);
  };

  // INSERT ITEM AT SPECIFIC INDEX DURING DRAG & DROP WITH NEW ROW SUPPORT & TOAST NOTIFICATION
  const handleInsertItemAtIndex = (
    targetSecId: string,
    targetIndex: number,
    data: any,
    isBeside: boolean = false
  ) => {
    let newItem: CanvasElement | null = null;
    let itemName = 'Element';

    if (data.type === 'key' && data.keyItem) {
      const k = data.keyItem;
      itemName = k.name || k.code;
      const keyTypeNorm = (k.keyType || '').toLowerCase();
      newItem = {
        id: `elem-${Date.now()}`,
        type: 'key',
        colSpan: keyTypeNorm === 'multiple' ? 6 : 3,
        forceNewRow: !isBeside,
        keyId: k.id || undefined,
        keyCode: k.code,
        keyName: k.name,
        keyType: k.keyType,
        showLabel: true,
        photoHeight: 'h-64',
        photoFit: 'object-cover',
        photoRounded: 'rounded-xl',
        galleryCols: 3,
      };
    } else if (data.type === 'custom' && data.elementType) {
      const elType: ElementType = data.elementType;
      if (elType === 'heading') {
        itemName = 'Heading Header';
        newItem = {
          id: `elem-${Date.now()}`,
          type: 'heading',
          colSpan: 6,
          forceNewRow: true,
          content: 'NEW SECTION HEADER',
          headingLevel: 'h2',
          uppercase: true,
        };
      } else if (elType === 'text') {
        itemName = 'Custom Text';
        newItem = {
          id: `elem-${Date.now()}`,
          type: 'text',
          colSpan: 3,
          forceNewRow: !isBeside,
          content: 'Custom text information block...',
          textSize: 'text-sm',
          fontWeight: 'font-normal',
        };
      } else if (elType === 'divider') {
        itemName = 'Divider';
        newItem = {
          id: `elem-${Date.now()}`,
          type: 'divider',
          colSpan: 6,
          forceNewRow: true,
        };
      }
    }

    if (newItem) {
      const createdItem = newItem;
      onUpdateSections(
        sections.map((sec) => {
          if (sec.id === targetSecId) {
            const newItems = sec.items.map((i) => ({ ...i }));
            const safeIndex = Math.max(0, Math.min(newItems.length, targetIndex));

            if (isBeside && newItems.length > 0) {
              createdItem.forceNewRow = false;
              const neighborIndex = Math.max(
                0,
                Math.min(newItems.length - 1, safeIndex === 0 ? 0 : safeIndex - 1)
              );
              const neighborItem = newItems[neighborIndex];
              if (neighborItem && createdItem.colSpan + neighborItem.colSpan > 6) {
                createdItem.colSpan = 3;
                neighborItem.colSpan = 3;
              }
              showNotification(`✨ Added "${itemName}" BESIDE on the same row!`);
            } else {
              createdItem.forceNewRow = true;
              showNotification(`🚀 Added "${itemName}" on a NEW ROW BELOW!`);
            }

            newItems.splice(safeIndex, 0, createdItem);
            return { ...sec, items: newItems };
          }
          return sec;
        })
      );
    }
  };

  // Section level Drag & Drop Handlers
  const handleDragOver = (e: React.DragEvent, secId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverSectionId(secId);
  };

  const handleDragLeave = (secId: string) => {
    if (dragOverSectionId === secId) {
      setDragOverSectionId(null);
    }
  };

  const handleDrop = (e: React.DragEvent, secId: string) => {
    e.preventDefault();
    setDragOverSectionId(null);

    let data = (window as any).__tb_dragged_item;
    if (!data) {
      const dataStr = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('application/json');
      if (dataStr) {
        try {
          data = JSON.parse(dataStr);
        } catch (err) { }
      }
    }

    if (!data) return;

    try {
      const targetSec = sections.find((s) => s.id === secId);
      if (!targetSec) return;

      if (data.type === 'canvasItem') {
        handleReorderItems(data.sourceSecId, secId, data.sourceIndex, targetSec.items.length, false);
      } else {
        handleInsertItemAtIndex(secId, targetSec.items.length, data, false);
      }
    } catch (err) {
      console.error('Failed to parse dropped element:', err);
    } finally {
      (window as any).__tb_dragged_item = null;
    }
  };

  return (
    <div className="card border shadow-sm h-100 d-flex flex-column tb-canvas-container position-relative">
      {/* TOAST NOTIFICATION BANNER */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="position-absolute start-50 translate-middle-x top-0 mt-2 px-3 py-2 rounded-pill bg-dark text-white shadow-lg d-flex align-items-center gap-2"
            style={{ zIndex: 1000, fontSize: '12px', fontWeight: 600 }}
          >
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CANVAS HEADER */}
      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-shrink-0">
        <h6 className="fw-bold mb-0 text-dark d-flex align-items-center">
          <i className="fas fa-th-large text-primary me-2"></i>
          Layout Canvas ({sections.length} Sections)
        </h6>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="tb-btn"
            onClick={onLoadSample}
            title="Load Prebuilt Layout Template"
          >
            Load Sample
          </button>
          <button
            type="button"
            className="tb-btn tb-btn-primary fw-semibold px-3"
            onClick={handleAddSection}
          >
            <i className="fas fa-plus me-1"></i> Add Section
          </button>
        </div>
      </div>

      {/* CANVAS CONTAINER - FIXED SCROLL HEIGHT */}
      <div className="card-body p-3 overflow-auto" style={{ height: '560px' }}>
        {sections.length === 0 ? (
          <div className="text-center py-5 border rounded-3 bg-white">
            <i className="fas fa-layer-group fa-3x text-muted mb-3 opacity-50"></i>
            <h6 className="fw-bold text-dark mb-1">Canvas is empty</h6>
            <p className="small text-muted mb-3">
              Add a section to start building your template, or click "Load Sample" to load a preset template.
            </p>
            <div className="d-flex justify-content-center gap-2">
              <button
                type="button"
                className="tb-btn tb-btn-primary px-3"
                onClick={handleAddSection}
              >
                + Add Section
              </button>
              <button
                type="button"
                className="tb-btn px-3"
                onClick={onLoadSample}
              >
                Load Sample Template
              </button>
            </div>
          </div>
        ) : (
          <AnimatePresence>
            <div className="d-flex flex-column gap-4">
              {sections.map((sec, secIdx) => {
                const isDragOver = dragOverSectionId === sec.id;
                const isCollapsed = !!collapsedSecIds[sec.id];
                return (
                  <motion.div
                    key={sec.id}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.2 }}
                    onDragOver={(e) => handleDragOver(e, sec.id)}
                    onDragLeave={() => handleDragLeave(sec.id)}
                    onDrop={(e) => handleDrop(e, sec.id)}
                    className={`tb-section-card tb-section-dropzone ${isDragOver ? 'is-drag-over' : ''
                      }`}
                  >
                    {/* SECTION BAR */}
                    <div className="card-header bg-light border-bottom p-2 px-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <div className="d-flex align-items-center gap-2">
                        {/* MINIMIZE / COLLAPSE TOGGLE BUTTON */}
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon"
                          onClick={() => toggleCollapseSection(sec.id)}
                          title={isCollapsed ? 'Expand Section' : 'Minimize Section'}
                        >
                          <i
                            className={`fas ${isCollapsed ? 'fa-plus text-primary' : 'fa-minus text-secondary'}`}
                            style={{ fontSize: '11px' }}
                          ></i>
                        </button>

                        <input
                          type="text"
                          className="form-control form-control-sm border-0 bg-transparent fw-bold text-dark px-1"
                          style={{ fontSize: '13px', width: '220px' }}
                          value={sec.title || ''}
                          placeholder="Section Title..."
                          onChange={(e) => handleSectionTitleChange(sec.id, e.target.value)}
                        />

                        {isCollapsed && (
                          <span
                            className="badge bg-white border text-secondary fw-semibold ms-1"
                            style={{ fontSize: '10px' }}
                          >
                            {sec.items.length} {sec.items.length === 1 ? 'item' : 'items'}
                          </span>
                        )}
                      </div>

                      {/* SECTION ACTION CONTROLS */}
                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon"
                          disabled={secIdx === 0}
                          onClick={() => handleMoveSection(secIdx, 'up')}
                          title="Move Up"
                        >
                          <i className="fas fa-chevron-up" style={{ fontSize: '11px' }}></i>
                        </button>
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon"
                          disabled={secIdx === sections.length - 1}
                          onClick={() => handleMoveSection(secIdx, 'down')}
                          title="Move Down"
                        >
                          <i className="fas fa-chevron-down" style={{ fontSize: '11px' }}></i>
                        </button>
                        <button
                          type="button"
                          className="tb-btn tb-btn-icon tb-btn-danger ms-1"
                          onClick={() => handleRemoveSection(sec.id)}
                          title="Delete Section"
                        >
                          <i className="fas fa-trash-alt" style={{ fontSize: '11px' }}></i>
                        </button>
                      </div>
                    </div>

                    {/* SECTION BODY - COLLAPSIBLE WITH ANIMATION */}
                    <AnimatePresence>
                      {!isCollapsed && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="card-body p-3"
                        >
                          {sec.items.length === 0 ? (
                            <div
                              className="text-center py-4 border-2 border-dashed rounded-3 text-muted"
                              style={{ backgroundColor: '#fcfcfd', borderColor: '#cbd5e1' }}
                            >
                              <i className="fas fa-cloud-upload-alt text-secondary mb-1"></i>
                              <p className="small mb-0 text-secondary">
                                Drag any key or element here. Drag to left/right edge to place beside, or bottom edge for a new row!
                              </p>
                            </div>
                          ) : (
                            <div className="row g-3">
                              <AnimatePresence>
                                {sec.items.map((item, itemIdx) => (
                                  <React.Fragment key={item.id}>
                                    {item.forceNewRow && itemIdx > 0 && (
                                      <div
                                        className="w-100 p-0 m-0"
                                        style={{ flexBasis: '100%', height: 0, overflow: 'hidden' }}
                                      ></div>
                                    )}
                                    <ResizableCanvasItem
                                      item={item}
                                      itemIndex={itemIdx}
                                      secCols={6}
                                      sectionId={sec.id}
                                      onUpdateSpan={handleUpdateSpan}
                                      onUpdateContent={handleUpdateContent}
                                      onEditElement={onEditElement}
                                      onToggleIf={handleToggleIf}
                                      onDuplicateItem={handleDuplicateItem}
                                      onRemoveItem={handleRemoveItem}
                                      onReorderItems={handleReorderItems}
                                      onInsertItemAtIndex={handleInsertItemAtIndex}
                                    />
                                  </React.Fragment>
                                ))}
                              </AnimatePresence>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default CanvasPanel;
